import { createSlice } from '@reduxjs/toolkit';

import { fetchSystemValid } from './system-thunk';
import { queryWorkOrder, changeStatus } from './work-order-thunk';

import { ArrayUtil } from 'crk-lib-tool';

function initialData() {
  return {
    work_date: new Date().toISOString(),
    work_status: 'N',
    due_date: null,
    specs: [{line_no: '1'}],
    formulas: [{line_no: '1'}],
    process: [{line_no: '1'}],
    jobs: [{}],
  }
}

export const workOrderSlice = createSlice({
  name: 'work-order',
  initialState: {
    enquiryLayout: [],
    entryTemplates: [],

    // Using for swich section / pane
    activeSection: 'search',
    activePane: '',

    // Using for search section
    searchData: {
      filter: '',
      status: '',
      found: [{}],      
    },

    // Using for data entry section
    data: initialData(),

    validWorkStatus: [],

    processBar: {
      show: false,
      value: 25
    }
  },

  // Actions will be dispatched by app => store
  reducers: {
    setEnquiryLaout: (state, action) => {
      state.enquiryLayout = action.payload;
    },

    setEntryTemplates: (state, action) => {
      state.entryTemplates = action.payload;
    },

    setActiveSection: (state, action) => {
      state.activeSection = action.payload;
    },

    setActivePane: (state, action) => {
      state.activePane = action.payload;
    },

    setSearchData: (state, action) => {
      state.searchData = action.payload;
    },

    setFound: (state, action) => {
      state.searchData.found = action.payload;
    },

    appendFound: (state, action) => {
      const _found = [ ...state.searchData.found, action.payload ];
      state.searchData.found = _found;
    },

    prependFound: (state, action) => {
      const _found = [ action.payload, ...state.searchData.found ];
      state.searchData.found = _found;
    },

    updateFound: (state, action) => {
      const _found = [ ...state.searchData.found ];
      const idx = _found.findIndex(x => x._id === action.payload._id);
      
      if(idx >= 0) {
        _found[idx] = action.payload;
        state.searchData.found = _found;
      }
    },

    removeFound: (state, action) => {
      state.searchData.found = state.searchData.found.filter((x) => x._id !== action.payload)
    },

    createNew: (state, action) => {
      const _data = initialData();

      state.data = _data;
    },

    setData: (state, action) => {
      state.data = action.payload;
    },

    updateData: (state, action) => {
      Object.keys(action.payload).forEach((k) => {
        state.data[k] = action.payload[k];
      })
    },

    // --------------
    // Specifications
    // --------------
    updateSpec: (state, action) => {
      const {idx, name, value} = action.payload;
      if (typeof value === 'object') {
        Object.keys(value).forEach((k) => {
          state.data.specs[idx][k] = value[k];
        })
      } else {
        state.data.specs[idx][name] = value;
      }
    },

    addSpec: (state, action) => {
      const _max = ArrayUtil.findMax(state.data.specs, 'line_no');
      state.data.specs.push({
        line_no: _max ? _max+1 : 1
      });
    },

    removeSpec: (state, action) => {
      state.data.specs = state.data.specs.filter((x,i) => i !== action.payload.idx)
    },
    // --------------------
    // END - Specifications
    // --------------------

    // --------------
    // Formulation
    // --------------
    updateFormula: (state, action) => {
      const {idx, name, value} = action.payload;
      if (typeof value === 'object') {
        Object.keys(value).forEach((k) => {
          state.data.formulas[idx][k] = value[k];
        })
      } else {
        state.data.formulas[idx][name] = value;
      }
    },

    addFormula: (state, action) => {
      const _max = ArrayUtil.findMax(state.data.formulas, 'line_no');
      state.data.formulas.push({
        line_no: _max ? _max+1 : 1
      });
    },

    removeFormula: (state, action) => {
      state.data.formulas = state.data.formulas.filter((x,i) => i !== action.payload.idx)
    },
    // --------------------
    // END - Formulation
    // --------------------    

    // --------------
    // Process
    // --------------
    updateProcess: (state, action) => {
      const {idx, name, value} = action.payload;
      if (typeof value === 'object') {
        Object.keys(value).forEach((k) => {
          state.data.process[idx][k] = value[k];
        })
      } else {
        state.data.process[idx][name] = value;
      }
    },

    addProcess: (state, action) => {
      const _max = ArrayUtil.findMax(state.data.process, 'line_no');
      state.data.process.push({
        line_no: _max ? _max+1 : 1
      });
    },

    removeProcess: (state, action) => {
      state.data.process = state.data.process.filter((x,i) => i !== action.payload.idx)
    },
    // --------------------
    // END - Process
    // --------------------

    // --------------
    // Job Status
    // --------------
    updateJob: (state, action) => {
      const {idx, name, value} = action.payload;
      if (typeof value === 'object') {
        Object.keys(value).forEach((k) => {
          state.data.jobs[idx][k] = value[k];
        })
      } else {
        state.data.jobs[idx][name] = value;
      }
    },

    addJob: (state, action) => {
      state.data.jobs.push({
        job_date: new Date()
      });
    },

    removeJob: (state, action) => {
      state.data.jobs = state.data.jobs.filter((x,i) => i !== action.payload.idx)
    },
    // --------------------
    // END - Job Status
    // --------------------

    showProcessBar: (state, action) => {
      state.processBar.show = action.payload
    },

    setProcessBar: (state, action) => {
      state.processBar = action.payload
    },

    setProcessBarValue: (state, action) => {
      state.processBar.value = action.payload
    }

  },

  extraReducers: (builder) => {
    builder.addCase(fetchSystemValid.fulfilled, (state, action) => {
      // console.info(action);
      if(action.meta.arg===241) {
        state.validWorkStatus = action.payload.data;
      }
    });

    builder.addCase(queryWorkOrder.fulfilled, (state, action) => {
      // console.info(action);
      state.searchData.found = action.payload;
    });

    builder.addCase(changeStatus.fulfilled, (state, action) => {
      // console.info(action);
      const _idx = state.searchData.found.findIndex((x) => x._id === action.payload._id);
      if (_idx >= 0) {
        state.searchData.found[_idx] = {...state.searchData.found[_idx], ...action.payload};
      }
    });    
  }
});

// Action creators are generated for each case reducer function
// export const { setEnquiryLaout, setEntryTemplates, setActivePane, setSearchData } = workOrderSlice.actions;
export const actions = workOrderSlice.actions;
export default workOrderSlice.reducer;
